import { useState } from 'react';
import {
  Button,
  Callout,
  Icon,
  IconButton,
  Input,
  Stack,
  Tag,
  Textarea,
  icons,
  theme,
} from '@agentisai/design-system';
import type { EvalDocument } from '@agentisai/shared-types';
import { DocumentPicker } from './DocumentPicker';
import { muted } from './format';
import { withName, type CaseDraft } from './setDraft';

const heading = {
  margin: 0,
  fontSize: theme.typography.fontSize.bodySm,
  fontWeight: theme.typography.fontWeight.semibold,
  color: theme.color.neutral.faint,
  textTransform: 'uppercase' as const,
  letterSpacing: theme.typography.letterSpacing.label,
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack as="section" aria-label={title} direction="column" gap={2}>
      <h4 style={heading}>{title}</h4>
      {children}
    </Stack>
  );
}
Section.displayName = 'Section';

/**
 * One case, top to bottom in the order a question-writer thinks: what the user
 * says, what they attach, what a good answer does (the rubric the AI judge
 * decides on, ADR-0073 §5), then the rarely-needed knobs behind Advanced.
 */
export function CaseEditor({
  draft,
  index,
  documents,
  canWrite,
  issues,
  onChange,
}: {
  draft: CaseDraft;
  index: number;
  documents: EvalDocument[];
  canWrite: boolean;
  issues: string[];
  onChange: (next: CaseDraft) => void;
}) {
  const [picking, setPicking] = useState(false);
  const set = <K extends keyof CaseDraft>(key: K, value: CaseDraft[K]) =>
    onChange({ ...draft, [key]: value });
  const known = new Set(documents.map((d) => d.name));
  const setupTurns = draft.original?.inputs.setup?.turns?.length ?? 0;
  const setTurn = (i: number, message: string) =>
    set(
      'turns',
      draft.turns.map((t, j) => (j === i ? { ...t, message } : t)),
    );

  return (
    <fieldset disabled={!canWrite} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
      <Stack direction="column" gap={5}>
        <Input
          label="Case name"
          placeholder="e.g. Answers the glass excess from the policy"
          value={draft.name}
          onChange={(e) => onChange(withName(draft, e.target.value, index))}
          helper={`ID: ${draft.id || '—'}`}
          fullWidth
        />

        {issues.length > 0 && (
          <Callout tone="warn">To save this case: {issues.join('; ')}.</Callout>
        )}

        <Section title="Conversation">
          {setupTurns > 0 && (
            <span style={muted}>
              {setupTurns} earlier message{setupTurns === 1 ? '' : 's'} replay first (kept as-is).
            </span>
          )}
          {draft.turns.length === 0 && (
            <span style={muted}>An end-to-end journey: Playwright runs it, not this runner.</span>
          )}
          {draft.turns.map((turn, i) => (
            <Stack key={i} direction="row" gap={2} align="start">
              <Textarea
                label={i === 0 ? 'The user asks' : `Then the user says (${i + 1})`}
                placeholder={i === 0 ? 'What would a real user ask?' : 'A follow-up…'}
                value={turn.message}
                onChange={(e) => setTurn(i, e.target.value)}
                autoResize
                maxRows={10}
                fullWidth
              />
              {canWrite && draft.turns.length > 1 && (
                <IconButton
                  aria-label={`Remove message ${i + 1}`}
                  icon={<Icon as={icons.Trash2} size="sm" />}
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    set(
                      'turns',
                      draft.turns.filter((_, j) => j !== i),
                    )
                  }
                />
              )}
            </Stack>
          ))}
          {canWrite && draft.turns.length > 0 && (
            <div>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Icon as={icons.MessageSquare} size="sm" />}
                onClick={() => set('turns', [...draft.turns, { message: '', base: {} }])}
              >
                Add a follow-up
              </Button>
            </div>
          )}
        </Section>

        <Section title="Attachments">
          {draft.documents.length === 0 && (
            <span style={muted}>No documents: the agent answers from the conversation alone.</span>
          )}
          {draft.documents.length > 0 && (
            <Stack direction="row" gap={1} wrap aria-label="Attached documents">
              {draft.documents.map((doc) => (
                <Tag
                  key={doc}
                  size="sm"
                  tone={known.has(doc) ? 'neutral' : 'danger'}
                  removable={canWrite}
                  onRemove={() =>
                    set(
                      'documents',
                      draft.documents.filter((d) => d !== doc),
                    )
                  }
                >
                  {known.has(doc) ? doc : `${doc} (missing)`}
                </Tag>
              ))}
            </Stack>
          )}
          {canWrite && (
            <div>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Icon as={icons.Paperclip} size="sm" />}
                onClick={() => setPicking(true)}
              >
                {draft.documents.length ? 'Change attachments' : 'Attach documents'}
              </Button>
            </div>
          )}
        </Section>

        <Section title="What a good answer does">
          <Textarea
            label="Rubric"
            placeholder="e.g. States the $100 glass-only excess (clause 3.2). Doesn't quote the $500 standard excess as the answer."
            helper="The AI judge reads every reply against this (plus the all-round rubric) and decides pass or fail."
            value={draft.judge}
            onChange={(e) => set('judge', e.target.value)}
            autoResize
            maxRows={12}
            fullWidth
          />
        </Section>

        <details>
          <summary style={{ cursor: 'pointer', ...muted }}>
            Advanced: ID, tags, pass score, exact-text checks
          </summary>
          <Stack direction="column" gap={3} style={{ marginTop: theme.space[3] }}>
            <Stack direction={{ sm: 'column', md: 'row' }} gap={3}>
              <Input
                label="Case ID"
                helper="Follows the name until you change it."
                value={draft.id}
                onChange={(e) => onChange({ ...draft, id: e.target.value, autoId: false })}
              />
              <Input
                label="Pass score"
                helper="0–1, used with the rubric. Blank = the run's --pass-score (0.7 unless set)."
                inputMode="decimal"
                value={draft.passScore}
                onChange={(e) => set('passScore', e.target.value)}
              />
            </Stack>
            <Input
              label="Tags"
              helper="Comma-separated, e.g. grounding, honesty. Run with --tag to pick them."
              value={draft.tags}
              onChange={(e) => set('tags', e.target.value)}
              fullWidth
            />
            <Stack direction={{ sm: 'column', md: 'row' }} gap={3}>
              <Textarea
                label="Reply must contain"
                helper="Optional. One regex per line; a miss fails the run."
                value={draft.matches}
                onChange={(e) => set('matches', e.target.value)}
                fullWidth
              />
              <Textarea
                label="Reply must not contain"
                helper="Optional. One regex per line."
                value={draft.notMatches}
                onChange={(e) => set('notMatches', e.target.value)}
                fullWidth
              />
            </Stack>
          </Stack>
        </details>
      </Stack>

      {picking && (
        <DocumentPicker
          documents={documents}
          selected={draft.documents}
          onClose={() => setPicking(false)}
          onDone={(next) => {
            set('documents', next);
            setPicking(false);
          }}
        />
      )}
    </fieldset>
  );
}
CaseEditor.displayName = 'CaseEditor';
